/**
 * Client helpers for the `trial_access_requests` table (see
 * supabase/migrations/20260717120000_create_trial_access_requests.sql).
 *
 * When a portal user requests access to a clinical trial, we persist a durable,
 * owner-scoped row here so the "Requested" state follows them across devices and
 * is server-queryable — instead of the old localStorage-only Set. The app still
 * keeps a localStorage cache (`requestedTrials`) for instant, synchronous reads
 * (Trials.js, the onboarding PatientInfoStep); `hydrateRequestedTrials()` unions
 * the server rows into that cache on login.
 *
 * Reads are RLS-scoped: a user sees only their own rows, admins see all. All
 * calls are forgiving when Supabase isn't configured (local dev) so the UI keeps
 * working, mirroring src/utils/crmContacts.js.
 */

import { supabase, isSupabaseConfigured } from "../lib/supabaseClient";

const TABLE = "trial_access_requests";
const CACHE_KEY = "requestedTrials";

const normalizeEmail = (email) => (email || "").trim().toLowerCase();

/** Read the localStorage cache as an array of trial-id strings. */
function readCache() {
  try {
    const stored = localStorage.getItem(CACHE_KEY);
    if (!stored) return [];
    const ids = JSON.parse(stored);
    return Array.isArray(ids) ? ids : [];
  } catch {
    return [];
  }
}

/** Write an array of trial-id strings to the localStorage cache. */
function writeCache(ids) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(Array.from(new Set(ids))));
  } catch {
    // Ignore quota / availability errors — the cache is best-effort.
  }
}

/**
 * Fetch the current user's requested trial ids from Supabase. RLS scopes the
 * result to the caller's own rows, so no email filter is needed.
 *
 * @returns {Promise<{ trialIds: string[], error: (Error|null) }>}
 */
export async function fetchMyTrialRequestIds() {
  if (!isSupabaseConfigured) return { trialIds: [], error: null };
  try {
    const { data, error } = await supabase.from(TABLE).select("trial_id");
    const trialIds = (data || [])
      .map((r) => r.trial_id)
      .filter((id) => typeof id === "string" && id);
    return { trialIds, error: error || null };
  } catch (err) {
    return { trialIds: [], error: err };
  }
}

/**
 * Admin: every trial access request one person has made, newest first. RLS
 * lets an admin read any row; the explicit email filter scopes the list to the
 * customer being inspected.
 *
 * @param {string} email
 * @returns {Promise<{ requests: Object[], error: (Error|null) }>}
 */
export async function listTrialRequestsForEmail(email) {
  const requester_email = normalizeEmail(email);
  if (!requester_email || !isSupabaseConfigured) {
    return { requests: [], error: null };
  }
  try {
    const { data, error } = await supabase
      .from(TABLE)
      .select("id, requester_email, trial_id, trial_name, status, created_at, updated_at")
      .eq("requester_email", requester_email)
      .order("created_at", { ascending: false });
    return { requests: data || [], error: error || null };
  } catch (err) {
    return { requests: [], error: err };
  }
}

/**
 * Pull the user's requests from Supabase and union them into the localStorage
 * cache. Called on login (AuthContext) and on the Trials page mount, so a fresh
 * device / cleared browser re-hydrates. Local ids are preserved (union), so an
 * offline request made before sync isn't lost.
 *
 * @returns {Promise<{ trialIds: string[], error: (Error|null) }>} merged ids.
 */
export async function hydrateRequestedTrials() {
  const local = readCache();
  if (!isSupabaseConfigured) return { trialIds: local, error: null };

  const { trialIds: remote, error } = await fetchMyTrialRequestIds();
  if (error) {
    // Keep the local cache as-is; surface the error for logging.
    return { trialIds: local, error };
  }

  const merged = Array.from(new Set([...local, ...remote]));
  writeCache(merged);
  return { trialIds: merged, error: null };
}

/**
 * Persist a trial access request for the given user. Idempotent: re-requesting
 * the same trial is a no-op (ON CONFLICT DO NOTHING via the unique key). The
 * localStorage cache is updated by the caller (Trials.js) for instant UI.
 *
 * @param {Object} params
 * @param {string} params.email    The requester's email (their own).
 * @param {string} params.trialId  The trial id being requested.
 * @param {string} [params.trialName] Display name, for admin readability.
 * @returns {Promise<{ error: (Error|null) }>}
 */
export async function addTrialRequest({ email, trialId, trialName } = {}) {
  const requester_email = normalizeEmail(email);
  if (!requester_email || !trialId) {
    return { error: new Error("addTrialRequest: email and trialId are required") };
  }

  if (!isSupabaseConfigured) return { error: null };

  try {
    const { error } = await supabase.from(TABLE).upsert(
      {
        requester_email,
        trial_id: trialId,
        trial_name: trialName || null,
      },
      { onConflict: "requester_email,trial_id", ignoreDuplicates: true }
    );
    if (error) {
      // eslint-disable-next-line no-console
      console.warn("addTrialRequest failed", error.message || error);
    }
    return { error: error || null };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("addTrialRequest threw", err);
    return { error: err };
  }
}
